import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { ExcursionCard } from "../components/ExcursionCard";
import { diffLabel, catLabel, distLabel } from "../lib/labels";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { breadcrumbLd } from "../lib/seo";

const seasonLabels = { spring: { ru: "Весна", en: "Spring" }, summer: { ru: "Лето", en: "Summer" }, autumn: { ru: "Осень", en: "Autumn" }, winter: { ru: "Зима", en: "Winter" } };

export default function Excursions() {
  const { t, lang } = useLang();
  const [excursions, setExcursions] = useState([]);
  const [places, setPlaces] = useState([]);
  const [f, setF] = useState({ season: "", difficulty: "", category: "", district: "" });

  useEffect(() => {
    api.get("/excursions").then((r) => setExcursions(r.data)).catch(() => {});
    api.get("/places").then((r) => setPlaces(r.data)).catch(() => {});
  }, []);

  const districtByPlace = useMemo(() => Object.fromEntries(places.map((p) => [p.id, p.district])), [places]);
  const categories = useMemo(() => [...new Set(excursions.map((e) => e.category))], [excursions]);
  const difficulties = useMemo(() => [...new Set(excursions.map((e) => e.difficulty))], [excursions]);
  const districts = useMemo(() => [...new Set(places.map((p) => p.district))], [places]);

  const filtered = useMemo(() => excursions.filter((e) => {
    if (f.season && !(e.seasons || []).includes(f.season)) return false;
    if (f.difficulty && e.difficulty !== f.difficulty) return false;
    if (f.category && e.category !== f.category) return false;
    if (f.district && !(e.place_ids || []).some((pid) => districtByPlace[pid] === f.district)) return false;
    return true;
  }), [excursions, f, districtByPlace]);

  const Chip = ({ active, onClick, children, testid }) => (
    <button data-testid={testid} onClick={onClick}
      className="text-[0.68rem] uppercase tracking-[0.14em] px-3.5 py-2 border transition-colors"
      style={{ borderColor: active ? "#A96818" : "rgba(52,33,22,0.25)", background: active ? "#A96818" : "transparent", color: active ? "#FAFAF7" : "#342116" }}>
      {children}
    </button>
  );

  return (
    <div data-testid="excursions-page" style={{ background: "#F4F0E7", minHeight: "100vh" }}>
      <Seo
        path="/excursions"
        lang={lang}
        title={lang === "ru" ? "Экскурсии по Алтаю" : "Altai excursions"}
        description={lang === "ru" ? "Каталог индивидуальных экскурсий АРГАЛИ по Республике Алтай: сезоны, сложность, категории и районы." : "АРГАЛИ private excursion catalogue across the Altai Republic — by season, difficulty, category and district."}
        jsonLd={breadcrumbLd([{ name: "АРГАЛИ", path: "/" }, { name: lang === "ru" ? "Экскурсии" : "Excursions", path: "/excursions" }])}
      />
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-32 pb-20">
        <Reveal>
          <p className="eyebrow" style={{ color: "#A96818" }}>{t("nav_excursions")}</p>
          <h1 className="display display-lg text-argali-obsidian mt-3">
            {lang === "ru" ? "Выберите" : "Choose"} <span className="text-argali-bronze">{lang === "ru" ? "свой Алтай." : "your Altai."}</span>
          </h1>
        </Reveal>

        <div className="mt-12 space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[0.62rem] uppercase tracking-[0.16em] text-argali-brown/50 w-20">{t("season")}</span>
            <Chip active={!f.season} onClick={() => setF({ ...f, season: "" })}>{t("all")}</Chip>
            {["spring", "summer", "autumn", "winter"].map((s) => (
              <Chip key={s} testid={`filter-season-${s}`} active={f.season === s} onClick={() => setF({ ...f, season: s })}>{seasonLabels[s][lang]}</Chip>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[0.62rem] uppercase tracking-[0.16em] text-argali-brown/50 w-20">{t("difficulty")}</span>
            <Chip active={!f.difficulty} onClick={() => setF({ ...f, difficulty: "" })}>{t("all")}</Chip>
            {difficulties.map((d) => (
              <Chip key={d} testid={`filter-diff-${d}`} active={f.difficulty === d} onClick={() => setF({ ...f, difficulty: d })}>{diffLabel(d, lang)}</Chip>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[0.62rem] uppercase tracking-[0.16em] text-argali-brown/50 w-20">{t("category")}</span>
            <Chip active={!f.category} onClick={() => setF({ ...f, category: "" })}>{t("all")}</Chip>
            {categories.map((c) => (
              <Chip key={c} testid={`filter-cat-${c}`} active={f.category === c} onClick={() => setF({ ...f, category: c })}>{catLabel(c, lang)}</Chip>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[0.62rem] uppercase tracking-[0.16em] text-argali-brown/50 w-20">{lang === "ru" ? "Район" : "District"}</span>
            <Chip active={!f.district} onClick={() => setF({ ...f, district: "" })}>{t("all")}</Chip>
            {districts.map((d) => (
              <Chip key={d} testid={`filter-district-${d}`} active={f.district === d} onClick={() => setF({ ...f, district: d })}>{distLabel(d, lang)}</Chip>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-32" data-testid="excursions-empty">
            <h3 className="font-serif text-3xl text-argali-obsidian">{t("empty_title")}</h3>
            <p className="text-argali-brown/70 mt-3">{t("empty_sub")}</p>
            <Link to="/custom-tour" className="btn-bronze group mt-8">{t("cta_custom")} <ArrowRight size={16} className="btn-arrow" /></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12" data-testid="excursions-grid">
            {filtered.map((e, i) => <ExcursionCard key={e.id} exc={e} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
